import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { ApiError, api, assetUrl, on, onDay, type ScenarioDetail, type ScenarioVerdict } from "../api";
import { DOT, Fold, INK, Loading, PageHead, Section, type Tone } from "../components/Look";
import Likeness from "../components/Likeness";
import { ScenarioText } from "../components/ScenarioReadable";
import { useTitle } from "../useTitle";

/**
 * One test case on its own page (step K4b, 2026-09-28), built to the approved
 * demo: the answer first, then what it expects beside how close it came, then
 * what it does beside its coverage and its cost by hand.
 *
 * It was a panel inside the list of 53 cases (5142 px on DisplayOS), with its
 * known problem under the full definition of its steps. The definition is
 * still here, folded, for whoever needs the steps as written.
 */
export default function CasePage() {
  const { t } = useTranslation();
  const params = useParams();
  const pid = Number(params.id);
  const sid = decodeURIComponent(params.caseId ?? "");
  const [d, setD] = useState<ScenarioDetail | null>(null);
  const [error, setError] = useState("");
  useTitle(sid);

  useEffect(() => {
    let alive = true;
    api
      .projectScenario(pid, sid)
      .then((x) => alive && setD(x))
      .catch((e) => alive && setError(e instanceof ApiError ? e.message : String(e)));
    return () => {
      alive = false;
    };
  }, [pid, sid]);

  if (error) {
    return (
      <section>
        <PageHead crumbs={[{ label: t("product.tabTests"), to: `/projects/${pid}/scenarios` }, { label: sid }]} title={sid} answer={error} bad />
      </section>
    );
  }
  if (!d) return <Loading label={t("common.loading")} />;

  const def = d.versions[0]?.definition;
  const history: ScenarioVerdict[] = Object.values(d.history).flat().sort((a, b) => b.at - a.at);
  const last = history[0];
  let answer: string;
  let bad = false;
  if (d.known) {
    answer = t("casePage.answerKnown", { date: onDay(d.known.expires_at) });
  } else if (!last) {
    answer = t("casePage.answerNone");
  } else if (last.verdict === "passed" || last.verdict === "xpassed") {
    answer = t("casePage.answerPassed", { run: last.run_name });
  } else if (last.verdict === "failed" || last.verdict === "error") {
    answer = t("casePage.answerFailed", { run: last.run_name });
    bad = true;
  } else {
    answer = t("casePage.answerOther", { run: last.run_name, verdict: last.verdict });
  }
  // What the newest comparison said about the mask, for the line under the
  // expected screen: "mask of 4 areas, 51% of the screen".
  const newestCmp = history.flatMap((h) => h.likeness ?? [])[0];
  const exp = (d.expected ?? [])[0];
  const tone = (v: string): Tone =>
    v === "passed" || v === "xpassed" ? "good" : v === "failed" || v === "error" ? "bad" : v === "xfailed" ? "warn" : "off";

  return (
    <section>
      <PageHead
        crumbs={[
          { label: d.project.name, to: `/projects/${pid}` },
          { label: t("product.tabTests"), to: `/projects/${pid}/scenarios` },
          { label: sid },
        ]}
        title={<span className="font-mono">{sid}</span>}
        answer={answer}
        bad={bad}
        after={
          <p>
            {def?.name}
            {d.known && (
              <>
                {". "}
                {d.known.reason}
                <span className="text-sm text-slate-500"> · {t("known.by", { who: d.known.created_by })}</span>
              </>
            )}
          </p>
        }
      />

      <div className="grid gap-x-6 lg:grid-cols-2 lg:items-start">
        {exp && (
          <Section card title={t("casePage.expects", { run: exp.run_name })}>
            <a href={assetUrl(`/api/v1/runs/${exp.run_id}/shots/${encodeURIComponent(exp.shot)}`)} target="_blank" rel="noreferrer noopener">
              <img
                src={assetUrl(`/api/v1/runs/${exp.run_id}/shots/${encodeURIComponent(exp.shot)}`)}
                alt={t("scenarios.expectedAlt", { golden: exp.golden || exp.shot })}
                className="w-full rounded-lg border border-slate-200 dark:border-slate-700"
              />
            </a>
            <p className="mt-1 text-sm text-slate-500">
              {exp.golden || exp.shot}
              {newestCmp && (newestCmp.mask_areas ?? 0) > 0 && newestCmp.masked_fraction != null
                ? ` · ${t("casePage.mask", { areas: newestCmp.mask_areas, pct: Math.round(newestCmp.masked_fraction * 100) })}`
                : ""}
            </p>
          </Section>
        )}
        {history.some((h) => (h.likeness ?? []).length) && (
          <Section card title={t("casePage.likeness")}>
            <Likeness items={history} titled />
          </Section>
        )}
      </div>

      <div className="grid gap-x-6 lg:grid-cols-2 lg:items-start">
        <Section card title={t("casePage.does")}>
          <dl className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-sm">
            {def?.group && (
              <>
                <dt className="text-slate-500">{t("casePage.group")}</dt>
                <dd>{def.group} {def.group_name}</dd>
              </>
            )}
            {(def?.verbs ?? []).length > 0 && (
              <>
                <dt className="text-slate-500">{t("casePage.steps")}</dt>
                <dd className="font-mono text-xs">{def!.verbs.join(" → ")}</dd>
              </>
            )}
            {(def?.requires ?? []).length > 0 && (
              <>
                <dt className="text-slate-500">{t("casePage.requires")}</dt>
                <dd className="flex flex-wrap gap-1">
                  {def!.requires.map((r) => (
                    <span key={r} className="rounded bg-slate-100 px-1.5 font-mono text-xs dark:bg-slate-800">{r}</span>
                  ))}
                </dd>
              </>
            )}
          </dl>
        </Section>
        <Section card title={t("casePage.coverage")}>
          <dl className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-sm">
            {d.quality && (
              <>
                <dt className="text-slate-500">{t("casePage.grade")}</dt>
                <dd>
                  <span className="mr-2 rounded bg-slate-100 px-1.5 font-semibold dark:bg-slate-800">{d.quality.grade}</span>
                  {d.quality.verdict}
                  {d.quality.stale && <span className="block text-xs text-amber-700 dark:text-amber-400">{t("caseCtx.stale")}</span>}
                </dd>
              </>
            )}
            {d.manual && (
              <>
                <dt className="text-slate-500">{t("casePage.byHand")}</dt>
                <dd>{t("casePage.byHandValue", { minutes: d.manual.minutes ?? "?", priority: d.manual.priority || "—" })}</dd>
                {d.manual.expected && (
                  <>
                    <dt className="text-slate-500">{t("casePage.expect")}</dt>
                    <dd>{d.manual.expected}</dd>
                  </>
                )}
              </>
            )}
            {!d.quality && !d.manual && <dd className="col-span-2 text-slate-500">{t("casePage.noCoverage")}</dd>}
          </dl>
        </Section>
      </div>

      <Section card title={t("casePage.history")}>
        {history.length ? (
          <ul className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
            {history.slice(0, 20).map((h) => (
              <li key={h.run_id}>
                <Link to={`/runs/${h.run_id}`} className="inline-flex items-baseline gap-1.5 font-semibold hover:underline">
                  <span aria-hidden="true" className={`inline-block h-2.5 w-2.5 self-center rounded-full ${DOT[tone(h.verdict)]}`} />
                  <span className={INK[tone(h.verdict)]}>{h.run_name}</span>
                  <span className="sr-only">{h.verdict}</span>
                </Link>
                <span className="ml-1 text-xs text-slate-500">{on(h.at)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-500">{t("scenarios.noHistory")}</p>
        )}
      </Section>

      {def?.source && (
        <Fold title={t("casePage.definition")}>
          <ScenarioText
            source={def.source}
            verbs={def.verbs}
            sourceClassName="mt-0.5 overflow-x-auto whitespace-pre-wrap break-words rounded bg-slate-50 p-2 font-mono text-xs leading-relaxed dark:bg-slate-800"
          />
        </Fold>
      )}
    </section>
  );
}
